import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import {
  Search,
  Plus,
  Download,
  Users,
  UserCheck,
  UserPlus,
  TrendingUp,
  Filter,
  Globe,
  Building2,
  MapPin,
  Mail,
  Phone,
  ExternalLink,
  LogOut,
  ChevronRight,
  X,
  Crosshair,
  Layers,
  BarChart3,
  BookOpen,
  Settings as SettingsIcon,
  Bell
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sizeLabels = {
  small: 'Piccola',
  medium: 'Media',
  large: 'Grande',
  enterprise: 'Enterprise'
};

const statusLabels = {
  new: 'Nuovo',
  contacted: 'Contattato',
  qualified: 'Qualificato',
  converted: 'Convertito',
  lost: 'Perso'
};

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showWebSearch, setShowWebSearch] = useState(false);
  const [webSearchResults, setWebSearchResults] = useState([]);
  const [webSearchLoading, setWebSearchLoading] = useState(false);
  const [webSearchParams, setWebSearchParams] = useState({ location: '', size: '', query: '' });
  const [unreadForm, setUnreadForm] = useState(0);

  // New lead form
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    location: '',
    size: 'medium',
    website: '',
    tags: []
  });

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (locationFilter) params.append('location', locationFilter);
      if (sizeFilter) params.append('size', sizeFilter);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API_URL}/leads?${params.toString()}`);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Errore nel caricamento dei lead');
    }
  }, [searchQuery, locationFilter, sizeFilter, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchUnreadForm = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/stats/form-leads-unread`);
      setUnreadForm(res.data.unread || 0);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchStats(), fetchUnreadForm()]);
      setLoading(false);
    };
    loadData();
    // Polling badge unread ogni 30s
    const t = setInterval(fetchUnreadForm, 30000);
    return () => clearInterval(t);
  }, [fetchLeads, fetchStats, fetchUnreadForm]);

  const handleAddLead = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/leads`, newLead);
      toast.success('Lead aggiunto con successo!');
      setShowAddModal(false);
      setNewLead({
        name: '',
        email: '',
        phone: '',
        company: '',
        position: '',
        location: '',
        size: 'medium',
        website: '',
        tags: []
      });
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast.error('Errore nell\'aggiunta del lead');
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (locationFilter) params.append('location', locationFilter);
      if (sizeFilter) params.append('size', sizeFilter);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API_URL}/leads/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export CSV completato!');
    } catch (error) {
      toast.error('Errore nell\'export');
    }
  };

  const handleWebSearch = async () => {
    setWebSearchLoading(true);
    try {
      const response = await axios.post(`${API_URL}/leads/search-web`, webSearchParams);
      setWebSearchResults(response.data);
    } catch (error) {
      toast.error('Errore nella ricerca');
    } finally {
      setWebSearchLoading(false);
    }
  };

  const handleSaveFromWebSearch = async (lead) => {
    try {
      await axios.post(`${API_URL}/leads`, {
        name: lead.name,
        email: lead.email,
        company: lead.company,
        location: lead.location,
        size: lead.size,
        website: lead.website,
        source: 'web_search'
      });
      toast.success(`${lead.name} salvato nei tuoi lead!`);
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const clearFilters = () => {
    setLocationFilter('');
    setSizeFilter('');
    setStatusFilter('');
    setSearchQuery('');
  };

  const hasActiveFilters = locationFilter || sizeFilter || statusFilter;

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img src="/86bit-logo.jpg" alt="86BIT" className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg object-contain bg-white ring-1 ring-slate-200 flex-shrink-0" data-testid="dashboard-logo" />
              <span className="font-bold text-base sm:text-xl text-slate-900 tracking-tight truncate">Customer Scout</span>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <span className="text-sm text-slate-600 hidden lg:block">
                Ciao, <span className="font-medium text-slate-900">{user?.name}</span>
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-slate-600 hover:text-slate-900"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Esci</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0 }}
          >
            <Card className="border-slate-200 card-hover" data-testid="stat-total">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Totale Lead</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.total || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <Card className="border-slate-200 card-hover" data-testid="stat-new">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Nuovi</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.by_status?.new || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <UserPlus className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-slate-200 card-hover" data-testid="stat-qualified">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Qualificati</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.by_status?.qualified || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card className="border-slate-200 card-hover" data-testid="stat-converted">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Convertiti</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.by_status?.converted || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Cerca per nome, azienda o email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 bg-white border-slate-200 w-full"
              data-testid="search-input"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              className={`h-11 ${hasActiveFilters ? 'border-blue-500 text-blue-600' : ''}`}
              variant="outline"
              data-testid="filter-toggle-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtri
              {hasActiveFilters && (
                <Badge className="ml-2 bg-blue-100 text-blue-700">
                  {[locationFilter, sizeFilter, statusFilter].filter(Boolean).length}
                </Badge>
              )}
            </Button>

            <Button
              onClick={() => navigate('/scraper')}
              className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="google-maps-scraper-btn"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Google Maps Scraper
            </Button>

            <Button
              onClick={() => navigate('/geo-search')}
              className="h-11 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="geo-search-btn"
            >
              <Crosshair className="w-4 h-4 mr-2" />
              Ricerca per Raggio
            </Button>

            <Button
              onClick={() => navigate('/company-lookup')}
              className="h-11 bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="company-lookup-btn"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Cerca Azienda
            </Button>

            <Button
              onClick={() => navigate('/batch-search')}
              className="h-11 bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="batch-search-btn"
            >
              <Layers className="w-4 h-4 mr-2" />
              Ricerca Batch
            </Button>

            <Button
              onClick={() => navigate('/pipeline')}
              className="h-11 bg-indigo-600 hover:bg-indigo-700 text-white relative"
              data-testid="pipeline-btn"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              CRM Pipeline
              {unreadForm > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-fuchsia-500 text-white text-[10px] font-bold ring-2 ring-white"
                  data-testid="unread-form-badge"
                  title={`${unreadForm} nuovi lead da form`}
                >
                  {unreadForm > 99 ? '99+' : unreadForm}
                </span>
              )}
            </Button>

            <Button
              onClick={() => navigate('/email-lookup')}
              className="h-11 bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="email-lookup-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Email Lookup
            </Button>

            <Button
              onClick={() => navigate('/email-directory')}
              className="h-11 bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="email-directory-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Rubrica Email
            </Button>

            <Button
              onClick={() => navigate('/revenue-search')}
              className="h-11 bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="revenue-search-btn"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Ricerca Fatturato
            </Button>

            <Button
              onClick={() => navigate('/settings')}
              variant="outline"
              className="h-11 relative"
              data-testid="settings-btn"
            >
              <SettingsIcon className="w-4 h-4 mr-2" />
              Impostazioni & Webhook
            </Button>

            <Dialog open={showWebSearch} onOpenChange={setShowWebSearch}>
              <DialogTrigger asChild>
                <Button variant="outline" className="h-11" data-testid="web-search-btn">
                  <Globe className="w-4 h-4 mr-2" />
                  Cerca Demo
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Cerca Potenziali Clienti</DialogTitle>
                  <DialogDescription>
                    Trova nuove aziende interessate al tuo business
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Posizione</Label>
                      <Input
                        placeholder="es. Milano, Roma..."
                        value={webSearchParams.location}
                        onChange={(e) => setWebSearchParams({...webSearchParams, location: e.target.value})}
                        data-testid="web-search-location"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Dimensione Azienda</Label>
                      <Select 
                        value={webSearchParams.size} 
                        onValueChange={(v) => setWebSearchParams({...webSearchParams, size: v})}
                      >
                        <SelectTrigger data-testid="web-search-size">
                          <SelectValue placeholder="Tutte" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutte</SelectItem>
                          <SelectItem value="small">Piccola</SelectItem>
                          <SelectItem value="medium">Media</SelectItem>
                          <SelectItem value="large">Grande</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={handleWebSearch} disabled={webSearchLoading} data-testid="web-search-submit">
                    {webSearchLoading ? 'Ricerca in corso...' : 'Cerca'}
                  </Button>

                  {webSearchResults.length > 0 && (
                    <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
                      {webSearchResults.map((result, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-900">{result.name}</p>
                            <p className="text-sm text-slate-500">{result.location} - {sizeLabels[result.size]}</p>
                          </div>
                          <Button 
                            size="sm" 
                            onClick={() => handleSaveFromWebSearch(result)}
                            data-testid={`save-result-${idx}`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Salva
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            <Button onClick={handleExportCSV} variant="outline" className="h-11" data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>

            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
              <DialogTrigger asChild>
                <Button className="h-11 bg-slate-900 hover:bg-slate-800" data-testid="add-lead-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Lead
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Aggiungi Nuovo Lead</DialogTitle>
                  <DialogDescription>
                    Inserisci i dettagli del potenziale cliente
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddLead} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nome *</Label>
                      <Input
                        required
                        value={newLead.name}
                        onChange={(e) => setNewLead({...newLead, name: e.target.value})}
                        placeholder="Nome completo"
                        data-testid="new-lead-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newLead.email}
                        onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                        placeholder="email@azienda.com"
                        data-testid="new-lead-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Telefono</Label>
                      <Input
                        value={newLead.phone}
                        onChange={(e) => setNewLead({...newLead, phone: e.target.value})}
                        placeholder="+39 123 456 7890"
                        data-testid="new-lead-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Azienda</Label>
                      <Input
                        value={newLead.company}
                        onChange={(e) => setNewLead({...newLead, company: e.target.value})}
                        placeholder="Nome azienda"
                        data-testid="new-lead-company"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Posizione *</Label>
                      <Input
                        required
                        value={newLead.location}
                        onChange={(e) => setNewLead({...newLead, location: e.target.value})}
                        placeholder="es. Milano"
                        data-testid="new-lead-location"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Dimensione *</Label>
                      <Select 
                        value={newLead.size} 
                        onValueChange={(v) => setNewLead({...newLead, size: v})}
                      >
                        <SelectTrigger data-testid="new-lead-size">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="small">Piccola</SelectItem>
                          <SelectItem value="medium">Media</SelectItem>
                          <SelectItem value="large">Grande</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Sito Web</Label>
                    <Input
                      value={newLead.website}
                      onChange={(e) => setNewLead({...newLead, website: e.target.value})}
                      placeholder="www.esempio.com"
                      data-testid="new-lead-website"
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-new-lead">
                    Aggiungi Lead
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6"
            >
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-4 items-end">
                    <div className="space-y-2 min-w-[180px]">
                      <Label className="text-sm text-slate-600">Posizione</Label>
                      <Input
                        placeholder="es. Milano"
                        value={locationFilter}
                        onChange={(e) => setLocationFilter(e.target.value)}
                        className="h-10"
                        data-testid="filter-location"
                      />
                    </div>
                    <div className="space-y-2 min-w-[180px]">
                      <Label className="text-sm text-slate-600">Dimensione</Label>
                      <Select value={sizeFilter} onValueChange={setSizeFilter}>
                        <SelectTrigger className="h-10" data-testid="filter-size">
                          <SelectValue placeholder="Tutte" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutte</SelectItem>
                          <SelectItem value="small">Piccola</SelectItem>
                          <SelectItem value="medium">Media</SelectItem>
                          <SelectItem value="large">Grande</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 min-w-[180px]">
                      <Label className="text-sm text-slate-600">Stato</Label>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-10" data-testid="filter-status">
                          <SelectValue placeholder="Tutti" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutti</SelectItem>
                          <SelectItem value="new">Nuovo</SelectItem>
                          <SelectItem value="contacted">Contattato</SelectItem>
                          <SelectItem value="qualified">Qualificato</SelectItem>
                          <SelectItem value="converted">Convertito</SelectItem>
                          <SelectItem value="lost">Perso</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {hasActiveFilters && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={clearFilters}
                        className="text-slate-500 hover:text-slate-700"
                        data-testid="clear-filters-btn"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Cancella filtri
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Leads List */}
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900">
              I tuoi Lead
              <span className="ml-2 text-sm font-normal text-slate-500">
                ({leads.length} {leads.length === 1 ? 'risultato' : 'risultati'})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner" />
              </div>
            ) : leads.length === 0 ? (
              <div className="text-center py-12 px-4">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">Nessun lead trovato</h3>
                <p className="text-slate-500 mb-4">
                  {hasActiveFilters 
                    ? 'Prova a modificare i filtri di ricerca'
                    : 'Inizia aggiungendo il tuo primo potenziale cliente'
                  }
                </p>
                {!hasActiveFilters && (
                  <Button onClick={() => setShowAddModal(true)} data-testid="empty-add-lead-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Aggiungi Lead
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {leads.map((lead, idx) => (
                  <motion.div
                    key={lead.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.03 }}
                    className="flex items-center justify-between p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/lead/${lead.id}`)}
                    data-testid={`lead-item-${lead.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-medium">
                        {lead.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-medium text-slate-900">{lead.name}</h3>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          {lead.company && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {lead.company}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {lead.location}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="hidden sm:flex items-center gap-2">
                        {lead.email && (
                          <Mail className="w-4 h-4 text-slate-400" />
                        )}
                        {lead.phone && (
                          <Phone className="w-4 h-4 text-slate-400" />
                        )}
                        {lead.website && (
                          <ExternalLink className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <Badge className={`size-${lead.size}`}>
                        {sizeLabels[lead.size]}
                      </Badge>
                      <Badge className={`status-${lead.status}`}>
                        {statusLabels[lead.status]}
                      </Badge>
                      <ChevronRight className="w-5 h-5 text-slate-300" />
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default DashboardPage;
